import { call, put } from 'redux-saga/effects';
import { get, map, set, omitBy } from 'lodash/fp';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import * as Api from '../../../../services/fbm-api';
import { sagaRoutineRequest } from '../../../../helpers/saga';
import {
  fetchInventoryItem as fetchInventoryItemRoutine,
  fetchInventoryItems as fetchInventoryItemsRoutine,
  createInventoryItem as createInventoryItemRoutine,
  updateInventoryItem as updateInventoryItemRoutine,
  deleteInventoryItem as deleteInventoryItemRoutine,
  importInventoryItem as importInventoryItemRoutine,
  fetchInventoryItemSkus as fetchInventoryItemSkusRoutine,
  addInventoryItemSku as addInventoryItemSkuRoutine,
  deleteInventoryItemSku as deleteInventoryItemSkuRoutine,
  importInventoryItemQuantity as importInventoryItemQuantityRoutine,
  fetchInventoryItemAvailability as fetchInventoryItemAvailabilityRoutine,
  fetchInventoryItemsAvailability as fetchInventoryItemsAvailabilityRoutine,
  fetchInventoryExportUrl as fetchInventoryExportUrlRoutine,
  fetchInventoryItemsTemplateDownloadUrl as fetchInventoryItemsTemplateDownloadUrlRoutine,
  deleteInventoryItemAvailability as deleteInventoryItemAvailabilityRoutine
} from './routines';
import history from '../../../../routes/history';
import createItemPathFor from '../../../../helpers/createItemPathFor';
import {
  addItemOption,
  updateItemOption,
  removeItemOption,
  resetInventoryItemDeleted,
  clearInventoryItemsAvailability,
  clearInventoryPackagesAvailability,
  clearInventoryAuditLogs
} from '../../actions';
import {
  failedImportItem,
  successImportItem
} from '../../actions/components/import-items-result';
import {
  CREATED as CREATED_IMPORT,
  UPDATED as UPDATED_IMPORT
} from '../../../../constants/inventory/ImportItemResultTypes';
import {
  failedImportItemQty,
  successImportItemQty
} from '../../actions/components/import-items-quantity-result';

export const fetchInventoryItem = ({ payload: { id } }) =>
  sagaRoutineRequest(fetchInventoryItemRoutine, {
    request: () => call(Api.fetchInventoryItem, id)
  });

export const fetchInventoryItems = ({ payload = {} }) => {
  const qs = omitBy(each => !each, payload);
  const createdAt = get('createdAt', qs);
  const formatDates = map(each => moment(each).format('YYYY-MM-DD'));
  const options = createdAt
    ? { qs: set('createdAt', formatDates(createdAt), qs) }
    : { qs };
  return sagaRoutineRequest(fetchInventoryItemsRoutine, {
    request: () => call(Api.fetchInventoryItems, options)
  });
};

export const createInventoryItem = ({ payload: { sku, ...body } }) =>
  sagaRoutineRequest(createInventoryItemRoutine, {
    request: () => call(Api.createInventoryItem, { body }),
    *success(response) {
      yield put(addItemOption(response));
      yield toastr.success('Success', `Item created.`);
      if (sku) {
        yield put(addInventoryItemSkuRoutine({ id: response.id, sku }));
      }
      history.push(createItemPathFor('view', response));
    }
  });

export const importInventoryItem = ({
  payload: { counter, id = null, sku, ...body }
}) =>
  sagaRoutineRequest(importInventoryItemRoutine, {
    request: () => {
      if (!id) return call(Api.createInventoryItem, { body });
      return call(Api.updateInventoryItem, { id, body });
    },
    *success(item) {
      const action = !id ? CREATED_IMPORT : UPDATED_IMPORT;
      yield put(addItemOption(item));
      yield put(successImportItem({ counter, action, item }));
    },
    *failure(error) {
      const action = !id ? CREATED_IMPORT : UPDATED_IMPORT;
      yield put(failedImportItem({ counter, action, item: body, error }));
    }
  });

export const updateInventoryItem = ({ payload: { id, body } }) =>
  sagaRoutineRequest(updateInventoryItemRoutine, {
    request: () => call(Api.updateInventoryItem, { id, body }),
    *success(response) {
      yield put(updateItemOption(response));
      yield toastr.success('Success', `Item updated.`);
    }
  });

export const deleteInventoryItem = ({ payload: { name, id } }) =>
  sagaRoutineRequest(deleteInventoryItemRoutine, {
    request: () => call(Api.deleteInventoryItem, id),
    successPayload: { id },
    *success() {
      yield put(removeItemOption(id));
      yield toastr.success('Success', `Deleted item "${name}".`);
    },
    *fulfill() {
      yield put(resetInventoryItemDeleted());
    }
  });

export const fetchInventoryItemAvailability = ({ payload: id }) =>
  sagaRoutineRequest(fetchInventoryItemAvailabilityRoutine, {
    request: () => call(Api.fetchInventoryItemAvailability, id),
    successPayload: { id }
  });

export const fetchInventoryItemsAvailability = ({ payload = {} }) => {
  const qs = omitBy(each => !each, payload);
  const createdAt = get('createdAt', qs);
  const formatDates = map(each => moment(each).format('YYYY-MM-DD'));
  const options = createdAt
    ? { qs: set('createdAt', formatDates(createdAt), qs) }
    : { qs };
  return sagaRoutineRequest(fetchInventoryItemsAvailabilityRoutine, {
    request: () => call(Api.fetchInventoryItemsAvailability, options)
  });
};

export const importInventoryItemQuantity = ({
  payload: { itemId, body, ...payload }
}) =>
  sagaRoutineRequest(importInventoryItemQuantityRoutine, {
    request: () => call(Api.adjustInventoryItem, { id: itemId, body }),
    *success() {
      const { quantity } = body;
      yield put(clearInventoryItemsAvailability());
      yield put(clearInventoryPackagesAvailability());
      yield put(clearInventoryAuditLogs());
      yield put(successImportItemQty({ ...payload, itemId, quantity }));
    },
    *failure(error) {
      const { quantity } = body;
      yield put(failedImportItemQty({ ...payload, itemId, quantity, error }));
    }
  });

export const deleteInventoryItemAvailability = ({ payload }) =>
  sagaRoutineRequest(deleteInventoryItemAvailabilityRoutine, {
    request: () =>
      call(Api.deleteInventoryItemAvailability, {
        id: get('item.id', payload),
        locationId: get('location.id', payload),
        zoneId: get('zone.id', payload)
      }),
    *success() {
      yield toastr.success(
        'Success',
        'Item availability successfully deleted.'
      );
    }
  });

export const fetchInventoryItemSkus = ({ payload: id }) =>
  sagaRoutineRequest(fetchInventoryItemSkusRoutine, {
    request: () => call(Api.fetchInventoryItemSkus, id)
  });

export const addInventoryItemSku = ({ payload: { id, sku } }) =>
  sagaRoutineRequest(addInventoryItemSkuRoutine, {
    request: () => call(Api.addInventoryItemSku, { id, body: { sku } }),
    *success() {
      yield toastr.success('Success', `Sku has been added.`);
    }
  });

export const deleteInventoryItemSku = ({ payload: { id, sku, skuId } }) =>
  sagaRoutineRequest(deleteInventoryItemSkuRoutine, {
    request: () => call(Api.deleteInventoryItemSku, { id, skuId }),
    successPayload: { id, skuId },
    *success() {
      yield toastr.success('Success', `Deleted sku "${sku}".`);
    }
  });

export const fetchInventoryItemsDetailsDownloadUrl = ({ payload = {} }) => {
  const qs = omitBy(each => !each, payload);
  const createdAt = get('createdAt', qs);
  const formatDates = map(each => moment(each).format('YYYY-MM-DD'));
  const options = createdAt
    ? { qs: set('createdAt', formatDates(createdAt), qs) }
    : { qs };
  return sagaRoutineRequest(fetchInventoryExportUrlRoutine, {
    request: () => call(Api.fetchInventoryExportUrl, options)
  });
};

export const fetchInventoryItemsTemplateDownloadUrl = () =>
  sagaRoutineRequest(fetchInventoryItemsTemplateDownloadUrlRoutine, {
    request: () => call(Api.fetchInventoryItemsTemplateDownloadUrl)
  });
